<template>
  <transition name="modal-fade">
    <div v-if="isOpen" class="modal-backdrop-custom">
      <div 
        class="modal fade show d-block" 
        tabindex="-1" 
        role="dialog"
        aria-modal="true"
        @click.self="close"
      >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content border-0 shadow-lg rounded-3">
            
            <div class="modal-header border-bottom-0 pb-0">
              <h5 class="modal-title fw-bold text-dark">
                <i :class="isEditing ? 'fas fa-edit' : 'fas fa-plus-circle'" class="text-primary me-2"></i>
                {{ isEditing ? 'Editar Marca' : 'Nueva Marca' }}
              </h5>
              <button type="button" class="btn-close" @click="close" aria-label="Close" :disabled="saving"></button>
            </div>

            <div class="modal-body p-4">
              <form @submit.prevent="saveBrand">
                
                <div class="mb-3">
                  <label class="form-label fw-bold text-secondary small text-uppercase">Nombre de Marca</label>
                  <div class="input-group">
                    <span class="input-group-text bg-light border-end-0 text-muted">
                      <i class="fas fa-tag"></i>
                    </span>
                    <input 
                      type="text" 
                      v-model="form.name" 
                      class="form-control border-start-0" 
                      placeholder="Ej: Toyota"
                      required 
                      :disabled="saving"
                    />
                  </div>
                </div>

                <div class="mb-3">
                  <label class="form-label fw-bold text-secondary small text-uppercase">Código</label>
                  <div class="input-group">
                    <span class="input-group-text bg-light border-end-0 text-muted">
                      <i class="fas fa-barcode"></i>
                    </span>
                    <input 
                      type="text" 
                      v-model="form.code" 
                      class="form-control border-start-0" 
                      placeholder="Ej: TOY-001"
                      :disabled="saving"
                    />
                  </div>
                </div>

                <div class="mb-4">
                  <label class="form-label fw-bold text-secondary small text-uppercase">Tipo de Vehículo</label>
                  <div class="input-group">
                    <span class="input-group-text bg-light border-end-0 text-muted">
                      <i class="fas fa-car"></i>
                    </span>
                    <select v-model="form.vehicule_type" class="form-select border-start-0" required :disabled="saving">
                      <option value="" disabled selected>Seleccione...</option>
                      <option v-for="t in vehiculeTypes" :key="t.value" :value="t.value">
                        {{ t.label }}
                      </option>
                    </select>
                  </div>
                </div>

                <div class="mb-4">
                   <FileUploadPreview
                      label="Logo de la Marca"
                      id="brand-logo"
                      accept=".jpg, .jpeg"
                      :file="form.logoFile"
                      :preview="form.logoPreview"
                      noFileMessage="Sin logo seleccionado"
                      @file-change="handleLogoChange"
                   />
                   <div class="form-text text-end mt-1" style="font-size: 0.75rem;">
                      Formato JPG - Máx 2MB
                   </div>
                </div>

                <div class="d-flex justify-content-end gap-2 pt-2 border-top">
                  <button type="button" class="btn btn-light border text-muted" @click="close" :disabled="saving">
                    Cancelar
                  </button>
                  <button type="submit" class="btn btn-primary px-4 shadow-sm" :disabled="saving">
                    <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    <i v-else class="fas fa-save me-1"></i>
                    {{ saving ? 'Guardando...' : (isEditing ? 'Guardar Cambios' : 'Crear Marca') }}
                  </button>
                </div>

              </form>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, watch, computed } from 'vue';
import Swal from 'sweetalert2';
import userConfirmation from '@/utils/userConfirmation.js';
import { createVehiculeBrand, updateVehiculeBrand } from '@/services/api/brandService.js';
import FileUploadPreview from '@/components/shared/FileUploadPreview.vue'; // Importar componente

export default {
  components: {
    FileUploadPreview
  },
  props: {
    isOpen: { type: Boolean, default: false },
    brand: { type: Object, default: null },
  },
  emits: ['close', 'success'],
  setup(props, { emit }) {
    const saving = ref(false);

    const form = ref({
      name: '',
      code: '',
      vehicule_type: '',
      logoFile: null,
      logoPreview: null,
    });

    const vehiculeTypes = ref([
      { value: 1, label: 'Autos' },
      { value: 2, label: 'Motos' },
      { value: 3, label: 'Camiones' },
      { value: 4, label: 'Maquinaria Agricola' },
      { value: 5, label: 'Repuestos' },
      { value: 6, label: 'Otros' },
      { value: 7, label: 'Semi Remolques' },
    ]);

    const isEditing = computed(() => !!props.brand);

    const backendBaseUrl = () => {
      const api = process.env.VUE_APP_ACARA_WEB_BACKEND_URL || '';
      return api.replace(/\/api\/v\d+\/?$/i, '');
    };

    const resolveLogoUrl = (logo) => {
      if (!logo) return null;
      if (logo.startsWith('blob:')) return logo;
      if (logo.startsWith('http://') || logo.startsWith('https://')) {
        if (logo.startsWith(window.location.origin)) {
          const path = logo.substring(window.location.origin.length);
          return backendBaseUrl() + path;
        }
        return logo;
      }
      const clean = logo.replace(/^\//, '');
      return backendBaseUrl() + '/' + clean;
    };

    const loadBrand = () => {

      form.value = {
        name: '',
        code: '',
        vehicule_type: '',
        logoFile: null,
        logoPreview: null,
      };

      if (props.brand) {
        form.value.name = props.brand.name || '';
        form.value.code = props.brand.code || '';
        form.value.vehicule_type = props.brand.vehicule_type || '';
        form.value.logoPreview = props.brand.logo ? resolveLogoUrl(props.brand.logo) : null;
      }
    };

    watch(() => props.brand, loadBrand, { immediate: true });
    watch(() => props.isOpen, (val) => { if (val) loadBrand(); });

    const close = () => {
      if (saving.value) return;
      emit('close');
    };

    const handleLogoChange = (file) => {
        form.value.logoFile = file;

    };

    const saveBrand = async () => {
      const actionText = isEditing.value ? "actualizar" : "crear";
      const confirmed = await userConfirmation(
          `¿Está seguro de ${actionText} esta marca?`
      );
      if (!confirmed) return;

      saving.value = true;
      
      Swal.fire({
        title: 'Guardando...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      try {
        const formData = new FormData();
        formData.append('name', form.value.name);
        if (form.value.code) formData.append('code', form.value.code);
        formData.append('vehicule_type', form.value.vehicule_type);
        
        if (form.value.logoFile) {
            formData.append('logo', form.value.logoFile);
        }

        if (isEditing.value) {
          await updateVehiculeBrand(props.brand.id, formData);
        } else {
          await createVehiculeBrand(formData);
        }

        Swal.fire({
            icon: 'success',
            title: 'Éxito',
            text: `Marca ${isEditing.value ? 'actualizada' : 'creada'} correctamente`
        });
        
        emit('success');
        emit('close');

      } catch (error) {
        console.error('Error saving brand:', error);
        let msg = 'Ocurrió un error al guardar la marca.';
        
        if (error.response?.data?.errors) {
           const firstKey = Object.keys(error.response.data.errors)[0];
           msg = error.response.data.errors[firstKey][0];
        }

        Swal.fire({ icon: 'error', title: 'Error', text: msg });
      } finally {
        saving.value = false;
      }
    };

    return { 
        form, 
        vehiculeTypes, 
        isEditing, 
        close, 
        saveBrand, 
        saving,
        handleLogoChange 
    };
  },
};
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: 1050;
  transition: opacity 0.3s ease;
}

.input-group-text {
  background-color: #f8f9fa;
  border-color: #ced4da;
}
.form-control, .form-select {
  border-color: #ced4da;
  box-shadow: none;
}
.form-control:focus, .form-select:focus {
  border-color: #86b7fe;
  background-color: #fff;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter-active .modal-dialog,
.modal-fade-leave-active .modal-dialog {
  transition: transform 0.3s ease-out;
}
.modal-fade-enter-from .modal-dialog {
  transform: translateY(-20px);
}
.modal-fade-leave-to .modal-dialog {
  transform: translateY(-20px);
}
</style>